import {
  Player,
  PlayerFiltersPosition,
  TeamCreateStep,
  TeamFormationProps,
} from '@dreamteam-monorepo/ui';
import { create } from 'zustand';
import type { TeamTransferPlayerType } from '@/lib/network/teams';
import { fromMoneyUnits, normalizeMoney, toMoneyUnits } from '@/utils/money';

export const MAX_TRANSFERS_PER_CONFIRMATION = 6;

export interface UseUserTeamState {
  backendTeamId: string | undefined;
  formation: TeamFormationProps['formation'] | undefined;
  slotPositionByIndex: Map<number, Exclude<PlayerFiltersPosition, 'ALL'>>;
  playersBySlot: Map<number, Player | undefined>;
  activeSlotIndex: number | undefined;
  teamName: string;
  isTeamSavedToDatabase: boolean;
  teamCreateStep: TeamCreateStep;
  captainPlayerId: Player['id'] | undefined;
  viceCaptainPlayerId: Player['id'] | undefined;
  transferRows: Array<{
    outPlayer: Player;
    inPlayer?: Player;
    confirmed?: boolean;
    playerType?: TeamTransferPlayerType;
  }>;
  selectedTabId: string;
  currentMatchdayHasStarted: boolean;
  backendBudgetRemaining: number | null;
  // Maintained for hydration compatibility. Pending saves now commit budget directly in the
  // backend, so refreshed teams should usually carry 0 here.
  backendPendingTransferCost: number;
  backendTransfersUsed: number | null;
}

export interface UseUserTeamActions {
  setBackendTeamId: (teamId: string | undefined) => void;
  setFormation: (_formation: TeamFormationProps['formation']) => void;
  setActiveSlot: (slotIndex: number | undefined) => void;
  addPlayerToSlot: (slotIndex: number, player: Player) => void;
  removePlayerFromSlot: (slotIndex: number) => void;
  clearAllPlayers: () => void;
  autoFillPlayers: (playersPool: Player[], budget: number) => void;
  togglePlayerForActiveSlot: (player: Player) => 'selected' | 'removed' | 'blocked';
  getPlayersInFormationOrder: () => Array<{
    slotIndex: number;
    position: Exclude<PlayerFiltersPosition, 'ALL'>;
    player: Player | undefined;
  }>;
  getSelectedPlayerIds: () => Array<Player['id']>;
  getPlayersByPosition: (position: Exclude<PlayerFiltersPosition, 'ALL'>) => Player[];
  getRemainingBudget: (budget: number) => number;
  setTeamName: (teamName: string) => void;
  setTeamSavedToDatabase: (saved: boolean) => void;
  saveConfirmedTeam: (teamName: string, budgetRemaining?: number | null) => void;
  setTeamCreateStep: (step: TeamCreateStep) => void;
  setCaptainPlayerId: (playerId: Player['id'] | undefined) => void;
  setViceCaptainPlayerId: (playerId: Player['id'] | undefined) => void;
  resetTeamCreateFlow: () => void;
  resetToFormationSelection: () => void;
  clearPersistedTeamData: () => void;
  setTransferRows: (
    rows: Array<{
      outPlayer: Player;
      inPlayer?: Player;
      confirmed?: boolean;
      playerType?: TeamTransferPlayerType;
    }>
  ) => void;
  addTransferOutRow: (player: Player, playerType?: TeamTransferPlayerType) => void;
  setTransferInForOutPlayer: (outPlayerId: Player['id'], inPlayer: Player) => void;
  markPendingTransfersConfirmed: () => void;
  applyConfirmedTransfers: (
    transfers: Array<{
      outPlayerId: Player['id'];
      inPlayer: Player;
    }>
  ) => void;
  hydrateFromBackendTeam: (payload: {
    teamId?: string;
    teamName: string;
    formation: TeamFormationProps['formation'];
    budgetRemaining: number | null;
    transfersUsed: number | null;
    pendingTransferCost: number;
    playersBySlot: Map<number, Player | undefined>;
    captainPlayerId: Player['id'] | undefined;
    viceCaptainPlayerId: Player['id'] | undefined;
  }) => void;
  setSelectedTabId: (tabId: string) => void;
  setCurrentMatchdayHasStarted: (hasStarted: boolean) => void;
  getIsSubstitutionTabActive: () => boolean;
}

export interface ConfirmedTransferPreviewInput {
  outPlayerId: Player['id'];
  inPlayer?: Player;
}

export interface TransferFormationPlayerInput {
  id: Player['id'];
  position: Player['position'];
}

export interface TransferFormationRowInput {
  outPlayer: TransferFormationPlayerInput;
  inPlayer?: TransferFormationPlayerInput;
  confirmed?: boolean;
}

export interface ConfirmedTransferPreviewState {
  formation: TeamFormationProps['formation'] | undefined;
  slotPositionByIndex: Map<number, Exclude<PlayerFiltersPosition, 'ALL'>>;
  playersBySlot: Map<number, Player | undefined>;
}

export const Formations: Array<{
  formation: TeamFormationProps['formation'];
  value: string;
}> = [
  {
    formation: [1, 4, 4, 2],
    value: '1-4-4-2',
  },
  {
    formation: [1, 4, 3, 3],
    value: '1-4-3-3',
  },
  {
    formation: [1, 4, 5, 1],
    value: '1-4-5-1',
  },
  {
    formation: [1, 3, 4, 3],
    value: '1-3-4-3',
  },
  {
    formation: [1, 3, 5, 2],
    value: '1-3-5-2',
  },
  {
    formation: [1, 5, 4, 1],
    value: '1-5-4-1',
  },
  {
    formation: [1, 5, 3, 2],
    value: '1-5-3-2',
  },
];

const DEFAULT_TEAM_NAME = 'My Team Name';
const DEFAULT_TEAM_TAB_ID = '1';
const CURRENT_MATCHDAY_TAB_ID = '1';
const LEGACY_USER_TEAM_STORAGE_KEY = 'user-team-store';
const FormationMap: Exclude<PlayerFiltersPosition, 'ALL'>[] = ['GK', 'DEF', 'MID', 'STR'];

const buildSlotMaps = (formation: TeamFormationProps['formation']) => {
  const slotPositionByIndex = new Map<number, Exclude<PlayerFiltersPosition, 'ALL'>>();
  const playersBySlot = new Map<number, Player | undefined>();

  let slotIndex = 0;
  formation.forEach((positionCount, rowIndex) => {
    const rowPosition = FormationMap[rowIndex];
    for (let idx = 0; idx < positionCount; idx += 1) {
      slotPositionByIndex.set(slotIndex, rowPosition);
      playersBySlot.set(slotIndex, undefined);
      slotIndex += 1;
    }
  });

  return {
    slotPositionByIndex,
    playersBySlot,
  };
};

const defaultFormation = Formations[0].formation;

const clearLegacyPersistedTeamData = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(LEGACY_USER_TEAM_STORAGE_KEY);
};

const createInitialState = (): UseUserTeamState => {
  clearLegacyPersistedTeamData();

  const fallbackSlotMaps = buildSlotMaps(defaultFormation);

  return {
    backendTeamId: undefined,
    formation: undefined,
    teamName: DEFAULT_TEAM_NAME,
    slotPositionByIndex: fallbackSlotMaps.slotPositionByIndex,
    playersBySlot: fallbackSlotMaps.playersBySlot,
    activeSlotIndex: undefined,
    isTeamSavedToDatabase: false,
    teamCreateStep: 'build',
    captainPlayerId: undefined,
    viceCaptainPlayerId: undefined,
    transferRows: [],
    selectedTabId: DEFAULT_TEAM_TAB_ID,
    currentMatchdayHasStarted: true,
    backendBudgetRemaining: null,
    backendPendingTransferCost: 0,
    backendTransfersUsed: null,
  };
};

const initState: UseUserTeamState = createInitialState();

const getPositionCountsFromPlayers = (players: Player[]) => {
  const counts = new Map<Exclude<PlayerFiltersPosition, 'ALL'>, number>();
  for (const player of players) {
    const position = player.position as Exclude<PlayerFiltersPosition, 'ALL'>;
    counts.set(position, (counts.get(position) ?? 0) + 1);
  }
  return counts;
};

const getFormationCapacity = (formation: TeamFormationProps['formation']) => {
  const slotMaps = buildSlotMaps(formation);
  const capacity = new Map<Exclude<PlayerFiltersPosition, 'ALL'>, number>();
  for (const [, slotPosition] of slotMaps.slotPositionByIndex.entries()) {
    capacity.set(slotPosition, (capacity.get(slotPosition) ?? 0) + 1);
  }
  return capacity;
};

const canFormationFitPositionCounts = (
  formation: TeamFormationProps['formation'],
  counts: Map<Exclude<PlayerFiltersPosition, 'ALL'>, number>
) => {
  const capacity = getFormationCapacity(formation);
  for (const [position, count] of counts.entries()) {
    if ((capacity.get(position) ?? 0) < count) {
      return false;
    }
  }
  return true;
};

const canAnyFormationFitPositionCounts = (
  counts: Map<Exclude<PlayerFiltersPosition, 'ALL'>, number>
) => {
  return Formations.some(({ formation }) => canFormationFitPositionCounts(formation, counts));
};

const adjustPositionCount = (
  counts: Map<Exclude<PlayerFiltersPosition, 'ALL'>, number>,
  position: Player['position'],
  delta: number
) => {
  const normalizedPosition = position as Exclude<PlayerFiltersPosition, 'ALL'>;
  counts.set(normalizedPosition, (counts.get(normalizedPosition) ?? 0) + delta);
};

const hasInvalidGoalkeeperTransfer = (transferRows: TransferFormationRowInput[]) => {
  return transferRows.some(row => {
    if (!row.inPlayer) {
      return false;
    }

    return row.outPlayer.position === 'GK'
      ? row.inPlayer.position !== 'GK'
      : row.inPlayer.position === 'GK';
  });
};

export const canTransferRowsFitFormation = ({
  teamPlayers,
  transferRows,
  requireComplete = false,
}: {
  teamPlayers: TransferFormationPlayerInput[];
  transferRows: TransferFormationRowInput[];
  requireComplete?: boolean;
}) => {
  const pendingRows = transferRows.filter(row => !row.confirmed);
  if (requireComplete && pendingRows.some(row => row.inPlayer === undefined)) {
    return false;
  }

  if (hasInvalidGoalkeeperTransfer(pendingRows)) {
    return false;
  }

  const counts = getPositionCountsFromPlayers(teamPlayers as Player[]);

  for (const row of pendingRows) {
    adjustPositionCount(counts, row.outPlayer.position, -1);
    if (row.inPlayer) {
      adjustPositionCount(counts, row.inPlayer.position, 1);
    }
  }

  return canAnyFormationFitPositionCounts(counts);
};

export const canTransferCandidateFitFormation = ({
  teamPlayers,
  transferRows,
  outPlayerId,
  inPlayer,
}: {
  teamPlayers: TransferFormationPlayerInput[];
  transferRows: TransferFormationRowInput[];
  outPlayerId: Player['id'];
  inPlayer: TransferFormationPlayerInput;
}) => {
  return canTransferRowsFitFormation({
    teamPlayers,
    transferRows: transferRows.map(row => {
      if (String(row.outPlayer.id) !== String(outPlayerId) || row.confirmed) {
        return row;
      }

      return {
        ...row,
        inPlayer,
      };
    }),
  });
};

export const canTransferCandidateKeepBatchCompletable = ({
  teamPlayers,
  transferRows,
  inPlayer,
}: {
  teamPlayers: TransferFormationPlayerInput[];
  transferRows: TransferFormationRowInput[];
  inPlayer: TransferFormationPlayerInput;
}) => {
  const pendingRows = transferRows.filter(row => !row.confirmed);
  const emptyRows = pendingRows.filter(row => !row.inPlayer);

  if (emptyRows.length === 0) {
    return false;
  }

  if (
    emptyRows.every(row =>
      row.outPlayer.position === 'GK' ? inPlayer.position !== 'GK' : inPlayer.position === 'GK'
    )
  ) {
    return false;
  }

  const counts = getPositionCountsFromPlayers(teamPlayers as Player[]);

  for (const row of pendingRows) {
    adjustPositionCount(counts, row.outPlayer.position, -1);
    if (row.inPlayer) {
      adjustPositionCount(counts, row.inPlayer.position, 1);
    }
  }

  adjustPositionCount(counts, inPlayer.position, 1);

  const remainingEmptyRows = emptyRows.length - 1;
  if (remainingEmptyRows <= 0) {
    return canAnyFormationFitPositionCounts(counts);
  }

  return Formations.some(({ formation }) => {
    const capacity = getFormationCapacity(formation);
    let missingPlayers = 0;

    for (const [position, positionCapacity] of capacity.entries()) {
      const count = counts.get(position) ?? 0;
      if (count > positionCapacity) {
        return false;
      }
      missingPlayers += positionCapacity - count;
    }

    return missingPlayers === remainingEmptyRows;
  });
};

const remapPlayersForFormation = (
  formation: TeamFormationProps['formation'],
  currentSlotPositionByIndex: Map<number, Exclude<PlayerFiltersPosition, 'ALL'>>,
  currentPlayersBySlot: Map<number, Player | undefined>
) => {
  const nextSlotMaps = buildSlotMaps(formation);
  const nextPlayersBySlot = new Map<number, Player | undefined>();

  const playersByPosition = new Map<Exclude<PlayerFiltersPosition, 'ALL'>, Player[]>();
  for (const [slotIndex, player] of currentPlayersBySlot.entries()) {
    if (!player) {
      continue;
    }
    const slotPosition = currentSlotPositionByIndex.get(slotIndex);
    if (!slotPosition) {
      continue;
    }
    const arr = playersByPosition.get(slotPosition) ?? [];
    arr.push(player);
    playersByPosition.set(slotPosition, arr);
  }

  for (const [, players] of playersByPosition.entries()) {
    players.sort((a, b) => String(a.id).localeCompare(String(b.id)));
  }

  for (const [slotIndex, slotPosition] of nextSlotMaps.slotPositionByIndex.entries()) {
    const arr = playersByPosition.get(slotPosition) ?? [];
    const nextPlayer = arr.shift();
    nextPlayersBySlot.set(slotIndex, nextPlayer);
    playersByPosition.set(slotPosition, arr);
  }

  return {
    slotPositionByIndex: nextSlotMaps.slotPositionByIndex,
    playersBySlot: nextPlayersBySlot,
  };
};

const placeFinalPlayersInFormation = (
  formation: TeamFormationProps['formation'],
  players: Player[],
  preferredSlotByPlayerId: Map<string, number>
) => {
  const nextSlotMaps = buildSlotMaps(formation);
  const playersBySlot = new Map(nextSlotMaps.playersBySlot);
  const unplacedPlayers = new Set(players);

  for (const player of players) {
    const preferredSlot = preferredSlotByPlayerId.get(String(player.id));
    if (
      preferredSlot === undefined ||
      nextSlotMaps.slotPositionByIndex.get(preferredSlot) !== player.position ||
      playersBySlot.get(preferredSlot) !== undefined
    ) {
      continue;
    }

    playersBySlot.set(preferredSlot, player);
    unplacedPlayers.delete(player);
  }

  for (const player of unplacedPlayers) {
    const availableSlot = [...nextSlotMaps.slotPositionByIndex.entries()]
      .filter(([, position]) => position === player.position)
      .map(([slotIndex]) => slotIndex)
      .find(slotIndex => playersBySlot.get(slotIndex) === undefined);

    if (availableSlot === undefined) {
      return undefined;
    }

    playersBySlot.set(availableSlot, player);
  }

  return {
    slotPositionByIndex: nextSlotMaps.slotPositionByIndex,
    playersBySlot,
  };
};

const resolveBestFormationRemap = (
  currentFormation: TeamFormationProps['formation'] | undefined,
  currentSlotPositionByIndex: Map<number, Exclude<PlayerFiltersPosition, 'ALL'>>,
  currentPlayersBySlot: Map<number, Player | undefined>,
  incomingPlayer: Player,
  options?: {
    allowOverflowDrops?: boolean;
  }
):
  | {
      formation: TeamFormationProps['formation'];
      slotPositionByIndex: Map<number, Exclude<PlayerFiltersPosition, 'ALL'>>;
      playersBySlot: Map<number, Player | undefined>;
    }
  | undefined => {
  const existingEntries = [...currentPlayersBySlot.entries()]
    .map(([slotIndex, player]) => {
      if (!player) return undefined;
      const position = currentSlotPositionByIndex.get(slotIndex);
      if (!position) return undefined;
      return { slotIndex, player, position };
    })
    .filter(
      (
        entry
      ): entry is {
        slotIndex: number;
        player: Player;
        position: Exclude<PlayerFiltersPosition, 'ALL'>;
      } => entry !== undefined
    );

  let best:
    | {
        removedCount: number;
        formation: TeamFormationProps['formation'];
        slotPositionByIndex: Map<number, Exclude<PlayerFiltersPosition, 'ALL'>>;
        playersBySlot: Map<number, Player | undefined>;
      }
    | undefined;

  for (const { formation } of Formations) {
    if (
      currentFormation &&
      formation.length === currentFormation.length &&
      formation.every((count, index) => count === currentFormation[index])
    ) {
      continue;
    }

    const capacity = getFormationCapacity(formation);
    const byPosition = new Map<Exclude<PlayerFiltersPosition, 'ALL'>, typeof existingEntries>();
    for (const entry of existingEntries) {
      const arr = byPosition.get(entry.position) ?? [];
      arr.push(entry);
      byPosition.set(entry.position, arr);
    }
    for (const [, entries] of byPosition.entries()) {
      entries.sort((left, right) => right.slotIndex - left.slotIndex);
    }

    const keptEntries: typeof existingEntries = [];
    let removedCount = 0;

    for (const [position, entries] of byPosition.entries()) {
      const cap = capacity.get(position) ?? 0;
      const reserveForIncoming = position === incomingPlayer.position ? 1 : 0;
      const allowedExistingCount = Math.max(0, cap - reserveForIncoming);
      const kept = entries.slice(0, allowedExistingCount);
      removedCount += Math.max(0, entries.length - kept.length);
      keptEntries.push(...kept);
    }

    if (removedCount > 0 && options?.allowOverflowDrops === false) {
      continue;
    }

    const keptPositionCounts = getPositionCountsFromPlayers(keptEntries.map(entry => entry.player));
    keptPositionCounts.set(
      incomingPlayer.position as Exclude<PlayerFiltersPosition, 'ALL'>,
      (keptPositionCounts.get(incomingPlayer.position as Exclude<PlayerFiltersPosition, 'ALL'>) ??
        0) + 1
    );

    if (!canFormationFitPositionCounts(formation, keptPositionCounts)) {
      continue;
    }

    const keptMap = new Map<number, Player | undefined>();
    for (const entry of keptEntries) {
      keptMap.set(entry.slotIndex, entry.player);
    }

    const remapped = remapPlayersForFormation(formation, currentSlotPositionByIndex, keptMap);
    const slotToAdd = [...remapped.slotPositionByIndex.entries()]
      .filter(([, position]) => position === incomingPlayer.position)
      .map(([slotIndex]) => slotIndex)
      .find(slotIndex => remapped.playersBySlot.get(slotIndex) === undefined);

    if (slotToAdd === undefined) {
      continue;
    }

    remapped.playersBySlot.set(slotToAdd, incomingPlayer);

    if (!best || removedCount < best.removedCount) {
      best = {
        removedCount,
        formation,
        slotPositionByIndex: remapped.slotPositionByIndex,
        playersBySlot: remapped.playersBySlot,
      };
    }
  }

  if (!best) {
    return undefined;
  }

  return {
    formation: best.formation,
    slotPositionByIndex: best.slotPositionByIndex,
    playersBySlot: best.playersBySlot,
  };
};

export const previewConfirmedTransfers = ({
  formation: initialFormation,
  slotPositionByIndex: initialSlotPositionByIndex,
  playersBySlot: initialPlayersBySlot,
  transfers,
}: ConfirmedTransferPreviewState & {
  transfers: ConfirmedTransferPreviewInput[];
}): ConfirmedTransferPreviewState => {
  const resolvedTransfers: Array<{
    outPlayerSlotIndex: number;
    outPlayerSlotPosition: Exclude<PlayerFiltersPosition, 'ALL'>;
    inPlayer?: Player;
  }> = [];

  for (const transfer of transfers) {
    let outPlayerSlotIndex: number | undefined;
    for (const [slotIndex, slotPlayer] of initialPlayersBySlot.entries()) {
      if (String(slotPlayer?.id ?? '') === String(transfer.outPlayerId)) {
        outPlayerSlotIndex = slotIndex;
        break;
      }
    }

    if (outPlayerSlotIndex === undefined) {
      continue;
    }

    const outPlayerSlotPosition = initialSlotPositionByIndex.get(outPlayerSlotIndex);
    if (!outPlayerSlotPosition) {
      continue;
    }

    const isPositionChange =
      transfer.inPlayer !== undefined && outPlayerSlotPosition !== transfer.inPlayer.position;
    if (
      isPositionChange &&
      transfer.inPlayer !== undefined &&
      (outPlayerSlotPosition === 'GK' || transfer.inPlayer.position === 'GK')
    ) {
      continue;
    }

    resolvedTransfers.push({
      outPlayerSlotIndex,
      outPlayerSlotPosition,
      inPlayer: transfer.inPlayer,
    });
  }

  if (resolvedTransfers.length === 0) {
    return {
      formation: initialFormation,
      slotPositionByIndex: initialSlotPositionByIndex,
      playersBySlot: initialPlayersBySlot,
    };
  }

  const hasCrossPositionTransfer = resolvedTransfers.some(
    transfer =>
      transfer.inPlayer !== undefined &&
      transfer.outPlayerSlotPosition !== transfer.inPlayer.position
  );

  if (!hasCrossPositionTransfer) {
    const playersBySlot = new Map(initialPlayersBySlot);

    for (const transfer of resolvedTransfers) {
      if (!transfer.inPlayer) {
        continue;
      }

      for (const [slotIndex, slotPlayer] of playersBySlot.entries()) {
        if (String(slotPlayer?.id ?? '') === String(transfer.inPlayer.id)) {
          playersBySlot.set(slotIndex, undefined);
        }
      }

      playersBySlot.set(transfer.outPlayerSlotIndex, transfer.inPlayer);
    }

    return {
      formation: initialFormation,
      slotPositionByIndex: initialSlotPositionByIndex,
      playersBySlot,
    };
  }

  const nextPlayersBySlot = new Map(initialPlayersBySlot);
  const outgoingSlotIndexes = new Set(
    resolvedTransfers.map(transfer => transfer.outPlayerSlotIndex)
  );
  const incomingPlayerIds = new Set(
    resolvedTransfers
      .map(transfer => transfer.inPlayer)
      .filter((player): player is Player => player !== undefined)
      .map(player => String(player.id))
  );

  for (const [slotIndex, slotPlayer] of nextPlayersBySlot.entries()) {
    if (
      outgoingSlotIndexes.has(slotIndex) ||
      (slotPlayer && incomingPlayerIds.has(String(slotPlayer.id)))
    ) {
      nextPlayersBySlot.set(slotIndex, undefined);
    }
  }

  const finalPlayers = [
    ...[...nextPlayersBySlot.values()].filter((player): player is Player => player !== undefined),
    ...resolvedTransfers
      .map(transfer => transfer.inPlayer)
      .filter((player): player is Player => player !== undefined),
  ];
  const finalPositionCounts = getPositionCountsFromPlayers(finalPlayers);
  const possibleFormations = [
    ...(initialFormation ? [{ formation: initialFormation, value: 'current' }] : []),
    ...Formations,
  ];
  const targetFormation = possibleFormations.find(({ formation }) => {
    return canFormationFitPositionCounts(formation, finalPositionCounts);
  })?.formation;

  if (!targetFormation) {
    return {
      formation: initialFormation,
      slotPositionByIndex: initialSlotPositionByIndex,
      playersBySlot: initialPlayersBySlot,
    };
  }

  const preferredSlotByPlayerId = new Map<string, number>();
  for (const [slotIndex, player] of initialPlayersBySlot.entries()) {
    if (player) {
      preferredSlotByPlayerId.set(String(player.id), slotIndex);
    }
  }
  for (const transfer of resolvedTransfers) {
    if (transfer.inPlayer) {
      preferredSlotByPlayerId.set(String(transfer.inPlayer.id), transfer.outPlayerSlotIndex);
    }
  }

  const remapped = placeFinalPlayersInFormation(
    targetFormation,
    finalPlayers,
    preferredSlotByPlayerId
  );
  if (!remapped) {
    return {
      formation: initialFormation,
      slotPositionByIndex: initialSlotPositionByIndex,
      playersBySlot: initialPlayersBySlot,
    };
  }

  return {
    formation: targetFormation,
    slotPositionByIndex: remapped.slotPositionByIndex,
    playersBySlot: remapped.playersBySlot,
  };
};

export const useUserTeamStore = create<UseUserTeamState & UseUserTeamActions>((set, get) => {
  return {
    ...initState,
    setBackendTeamId: teamId => {
      set({
        backendTeamId: teamId,
      });
    },
    setSelectedTabId: tabId => {
      set(state => {
        const isTabChanging = state.selectedTabId !== tabId;
        // Keep confirmed rows only when moving between tabs so pending substitution/transfer
        // selections never leak into a different tab mode.
        const transferRows = isTabChanging
          ? state.transferRows.filter(row => row.confirmed)
          : state.transferRows;

        return {
          selectedTabId: tabId,
          transferRows,
        };
      });
    },
    setCurrentMatchdayHasStarted: hasStarted => {
      set({
        currentMatchdayHasStarted: hasStarted,
      });
    },
    getIsSubstitutionTabActive: () => {
      const state = get();
      return state.currentMatchdayHasStarted && state.selectedTabId === CURRENT_MATCHDAY_TAB_ID;
    },
    setFormation: _formation => {
      const slotMaps = buildSlotMaps(_formation);
      set({
        formation: _formation,
        slotPositionByIndex: slotMaps.slotPositionByIndex,
        playersBySlot: slotMaps.playersBySlot,
        activeSlotIndex: undefined,
        isTeamSavedToDatabase: false,
        teamCreateStep: 'build',
        captainPlayerId: undefined,
        viceCaptainPlayerId: undefined,
        transferRows: [],
        backendBudgetRemaining: null,
        backendPendingTransferCost: 0,
        backendTransfersUsed: null,
      });
    },
    setActiveSlot: slotIndex => {
      set({
        activeSlotIndex: slotIndex,
      });
    },
    addPlayerToSlot: (slotIndex, player) => {
      set(state => {
        const slotPosition = state.slotPositionByIndex.get(slotIndex);
        if (!slotPosition || slotPosition !== player.position) {
          return {};
        }

        const playersBySlot = new Map(state.playersBySlot);
        playersBySlot.forEach((currentPlayer, currentSlotIndex) => {
          if (currentPlayer?.id === player.id) {
            playersBySlot.set(currentSlotIndex, undefined);
          }
        });
        playersBySlot.set(slotIndex, player);

        return {
          playersBySlot,
          isTeamSavedToDatabase: false,
          teamCreateStep: 'build',
          captainPlayerId: undefined,
          viceCaptainPlayerId: undefined,
        };
      });
    },
    removePlayerFromSlot: slotIndex => {
      set(state => {
        if (!state.slotPositionByIndex.has(slotIndex)) {
          return {};
        }

        const playersBySlot = new Map(state.playersBySlot);
        playersBySlot.set(slotIndex, undefined);

        return {
          playersBySlot,
          isTeamSavedToDatabase: false,
          teamCreateStep: 'build',
          captainPlayerId: undefined,
          viceCaptainPlayerId: undefined,
        };
      });
    },
    clearAllPlayers: () => {
      set(state => {
        const playersBySlot = new Map<number, Player | undefined>();
        state.slotPositionByIndex.forEach((_, slotIndex) => {
          playersBySlot.set(slotIndex, undefined);
        });

        return {
          playersBySlot,
          activeSlotIndex: undefined,
          isTeamSavedToDatabase: false,
          teamCreateStep: 'build',
          captainPlayerId: undefined,
          viceCaptainPlayerId: undefined,
        };
      });
    },
    autoFillPlayers: (playersPool, budget) => {
      set(state => {
        if (!playersPool.length) {
          return {};
        }

        const playersBySlot = new Map<number, Player | undefined>();
        const usedPlayerIds = new Set<Player['id']>();
        const selectedPrices: number[] = [];

        state.slotPositionByIndex.forEach((_, slotIndex) => {
          const selectedPlayer = state.playersBySlot.get(slotIndex);
          playersBySlot.set(slotIndex, selectedPlayer);

          if (!selectedPlayer) {
            return;
          }

          usedPlayerIds.add(selectedPlayer.id);
          const selectedPrice = Number(selectedPlayer.price);
          if (Number.isFinite(selectedPrice) && selectedPrice > 0) {
            selectedPrices.push(selectedPrice);
          }
        });

        const emptySlots = [...state.slotPositionByIndex.entries()].filter(
          ([slotIndex]) => !playersBySlot.get(slotIndex)
        );

        if (emptySlots.length === 0) {
          return {};
        }

        // Normalise the budget into the same unit as player prices (see
        // getRemainingBudget): some feeds use base units (e.g. 50_000_000) while
        // prices are in display units (e.g. 7.5).
        const availablePrices = playersPool
          .map(player => Number(player.price))
          .filter(price => Number.isFinite(price) && price > 0);
        const hasDisplayUnitPrices = [...selectedPrices, ...availablePrices].some(
          price => price < 1000
        );
        const hasBaseUnitBudget = budget >= 1_000_000;
        const normalizedBudget =
          hasBaseUnitBudget && hasDisplayUnitPrices ? budget / 1_000_000 : budget;

        // Priced, available candidates per position, cheapest first.
        const candidatesByPosition = new Map<string, Player[]>();
        for (const player of playersPool) {
          if (typeof player.price !== 'number' || !Number.isFinite(player.price)) {
            continue;
          }
          const list = candidatesByPosition.get(player.position) ?? [];
          list.push(player);
          candidatesByPosition.set(player.position, list);
        }
        for (const list of candidatesByPosition.values()) {
          list.sort((a, b) => a.price - b.price);
        }

        // Process slots in a random order so the high-value picks are not always
        // concentrated on the same positions, while still spending most of the budget.
        const slots = [...emptySlots];
        for (let i = slots.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [slots[i], slots[j]] = [slots[j], slots[i]];
        }

        const remainingByPosition = new Map<string, number>();
        for (const [, position] of slots) {
          remainingByPosition.set(position, (remainingByPosition.get(position) ?? 0) + 1);
        }

        let spentUnits = selectedPrices.reduce((total, price) => total + toMoneyUnits(price), 0);

        for (const [slotIndex, position] of slots) {
          remainingByPosition.set(position, (remainingByPosition.get(position) ?? 0) - 1);

          const candidates = (candidatesByPosition.get(position) ?? []).filter(
            player => !usedPlayerIds.has(player.id)
          );
          if (candidates.length === 0) {
            continue;
          }

          // Reserve the cheapest available players needed to fill the other
          // still-empty slots, so we never overspend and leave a slot unfillable.
          let reserveUnits = 0;
          for (const [otherPosition, need] of remainingByPosition) {
            if (need <= 0) {
              continue;
            }
            const otherAvailable = (candidatesByPosition.get(otherPosition) ?? []).filter(
              player => !usedPlayerIds.has(player.id)
            );
            for (let i = 0; i < Math.min(need, otherAvailable.length); i += 1) {
              reserveUnits += toMoneyUnits(otherAvailable[i].price);
            }
          }

          const maxSpendForSlotUnits = toMoneyUnits(normalizedBudget) - spentUnits - reserveUnits;

          // Pick the most expensive affordable player; fall back to the cheapest
          // if nothing fits (keeps the team valid even if slightly over budget).
          let selectedPlayer = candidates[0];
          for (let i = candidates.length - 1; i >= 0; i -= 1) {
            if (toMoneyUnits(candidates[i].price) <= maxSpendForSlotUnits) {
              selectedPlayer = candidates[i];
              break;
            }
          }

          usedPlayerIds.add(selectedPlayer.id);
          spentUnits += toMoneyUnits(selectedPlayer.price);
          playersBySlot.set(slotIndex, selectedPlayer);
        }

        return {
          playersBySlot,
          activeSlotIndex: undefined,
          isTeamSavedToDatabase: false,
          teamCreateStep: 'build',
          captainPlayerId: undefined,
          viceCaptainPlayerId: undefined,
        };
      });
    },
    togglePlayerForActiveSlot: player => {
      const state = get();
      const playersBySlot = new Map(state.playersBySlot);
      let existingPlayerSlotIndex: number | undefined;

      for (const [slotIndex, slotPlayer] of playersBySlot.entries()) {
        if (slotPlayer?.id === player.id) {
          existingPlayerSlotIndex = slotIndex;
          break;
        }
      }

      if (existingPlayerSlotIndex !== undefined) {
        const activeSlotIndex = state.activeSlotIndex;
        const canUseActiveSlotForExistingPlayer =
          activeSlotIndex !== undefined &&
          state.slotPositionByIndex.get(activeSlotIndex) === player.position;

        if (canUseActiveSlotForExistingPlayer && activeSlotIndex !== existingPlayerSlotIndex) {
          playersBySlot.set(existingPlayerSlotIndex, undefined);
          playersBySlot.set(activeSlotIndex, player);
          set({
            playersBySlot,
            isTeamSavedToDatabase: false,
            teamCreateStep: 'build',
            captainPlayerId: undefined,
            viceCaptainPlayerId: undefined,
          });
          return 'selected';
        }

        playersBySlot.set(existingPlayerSlotIndex, undefined);
        set({
          playersBySlot,
          isTeamSavedToDatabase: false,
          teamCreateStep: 'build',
          captainPlayerId: undefined,
          viceCaptainPlayerId: undefined,
        });
        return 'removed';
      }

      const canUseActiveSlot =
        state.activeSlotIndex !== undefined &&
        state.slotPositionByIndex.get(state.activeSlotIndex) === player.position;

      if (canUseActiveSlot) {
        playersBySlot.set(state.activeSlotIndex, player);
        set({
          playersBySlot,
          isTeamSavedToDatabase: false,
          teamCreateStep: 'build',
          captainPlayerId: undefined,
          viceCaptainPlayerId: undefined,
        });
        return 'selected';
      }

      // If a slot is explicitly selected but the new player's position is different,
      // treat this as replacing the selected slot occupant first (if any), then
      // continue with position-based add/remap. This avoids dropping an arbitrary
      // same-position player (e.g. right back) during formation changes.
      if (state.activeSlotIndex !== undefined) {
        const activeSlotPosition = state.slotPositionByIndex.get(state.activeSlotIndex);
        const activeSlotPlayer = playersBySlot.get(state.activeSlotIndex);
        if (
          activeSlotPosition &&
          activeSlotPosition !== player.position &&
          activeSlotPlayer !== undefined
        ) {
          playersBySlot.set(state.activeSlotIndex, undefined);
        }
      }

      const matchingSlots = [...state.slotPositionByIndex.entries()]
        .filter(([, slotPosition]) => slotPosition === player.position)
        .map(([slotIndex]) => slotIndex);
      const firstEmptyMatchingSlot = matchingSlots.find(slotIndex => {
        return playersBySlot.get(slotIndex) === undefined;
      });

      if (firstEmptyMatchingSlot !== undefined) {
        playersBySlot.set(firstEmptyMatchingSlot, player);
        set({
          playersBySlot,
          isTeamSavedToDatabase: false,
          teamCreateStep: 'build',
          captainPlayerId: undefined,
          viceCaptainPlayerId: undefined,
        });
        return 'selected';
      }

      // GK is a fixed singleton role across all supported formations.
      // If the GK slot is already full, do not attempt formation remap.
      if (player.position === 'GK') {
        return 'blocked';
      }

      // No free slot for this position in current formation:
      // try to auto-switch to a compatible formation and keep as many
      // existing players as possible (dropping last slotted overflowing players).
      const remappedSelection = resolveBestFormationRemap(
        state.formation,
        state.slotPositionByIndex,
        playersBySlot,
        player
      );

      if (!remappedSelection) {
        return 'blocked';
      }
      set({
        formation: remappedSelection.formation,
        slotPositionByIndex: remappedSelection.slotPositionByIndex,
        playersBySlot: remappedSelection.playersBySlot,
        activeSlotIndex: undefined,
        isTeamSavedToDatabase: false,
        teamCreateStep: 'build',
        captainPlayerId: undefined,
        viceCaptainPlayerId: undefined,
      });
      return 'selected';
    },
    getPlayersInFormationOrder: () => {
      const state = get();

      return [...state.slotPositionByIndex.entries()].map(([slotIndex, position]) => {
        return {
          slotIndex,
          position,
          player: state.playersBySlot.get(slotIndex),
        };
      });
    },
    getSelectedPlayerIds: () => {
      const state = get();

      return [...state.playersBySlot.values()]
        .map(player => player?.id)
        .filter((id): id is Player['id'] => id !== undefined);
    },
    getPlayersByPosition: position => {
      const state = get();

      return [...state.slotPositionByIndex.entries()].reduce<Player[]>(
        (players, [slotIndex, slotPosition]) => {
          if (slotPosition !== position) {
            return players;
          }

          const player = state.playersBySlot.get(slotIndex);
          if (player) {
            players.push(player);
          }

          return players;
        },
        []
      );
    },
    getRemainingBudget: budget => {
      const state = get();

      const selectedPrices = [...state.playersBySlot.values()]
        .map(player => Number(player?.price ?? 0))
        .filter(price => Number.isFinite(price) && price > 0);

      // Some feeds return budgets in base units (e.g. 50_000_000) while
      // player prices are already in "millions" display units (e.g. 7.5).
      // Normalize to the same unit so UI budget updates correctly.
      const hasBaseUnitBudget = budget >= 1_000_000;
      const hasDisplayUnitPrices = selectedPrices.some(price => price < 1_000);
      const normalizeBaseToDisplay = hasBaseUnitBudget && hasDisplayUnitPrices;

      const normalizedBudget = normalizeBaseToDisplay ? budget / 1_000_000 : budget;

      const remainingBudgetUnits = selectedPrices.reduce(
        (total, price) => total - toMoneyUnits(price),
        toMoneyUnits(normalizedBudget)
      );

      return normalizeMoney(fromMoneyUnits(remainingBudgetUnits));
    },
    setTeamName: teamName => {
      set({
        teamName,
        isTeamSavedToDatabase: false,
      });
    },
    setTeamSavedToDatabase: saved => {
      set({
        isTeamSavedToDatabase: saved,
      });
    },
    saveConfirmedTeam: (teamName, budgetRemaining) => {
      const normalizedTeamName = teamName.trim() || DEFAULT_TEAM_NAME;
      set(state => ({
        teamName: normalizedTeamName,
        isTeamSavedToDatabase: true,
        backendBudgetRemaining:
          budgetRemaining === undefined ? state.backendBudgetRemaining : budgetRemaining,
      }));
    },
    setTeamCreateStep: step => {
      set({
        teamCreateStep: step,
      });
    },
    setCaptainPlayerId: playerId => {
      set({
        captainPlayerId: playerId,
      });
    },
    setViceCaptainPlayerId: playerId => {
      set({
        viceCaptainPlayerId: playerId,
      });
    },
    resetTeamCreateFlow: () => {
      set({
        teamCreateStep: 'build',
        captainPlayerId: undefined,
        viceCaptainPlayerId: undefined,
      });
    },
    resetToFormationSelection: () => {
      const slotMaps = buildSlotMaps(defaultFormation);
      set(state => ({
        formation: undefined,
        slotPositionByIndex: slotMaps.slotPositionByIndex,
        playersBySlot: slotMaps.playersBySlot,
        activeSlotIndex: undefined,
        isTeamSavedToDatabase: false,
        teamCreateStep: 'build',
        captainPlayerId: undefined,
        viceCaptainPlayerId: undefined,
        transferRows: state.transferRows.filter(row => row.confirmed),
      }));
    },
    clearPersistedTeamData: () => {
      clearLegacyPersistedTeamData();

      const slotMaps = buildSlotMaps(defaultFormation);
      set({
        backendTeamId: undefined,
        formation: undefined,
        slotPositionByIndex: slotMaps.slotPositionByIndex,
        playersBySlot: slotMaps.playersBySlot,
        activeSlotIndex: undefined,
        teamName: DEFAULT_TEAM_NAME,
        isTeamSavedToDatabase: false,
        teamCreateStep: 'build',
        captainPlayerId: undefined,
        viceCaptainPlayerId: undefined,
        transferRows: [],
        backendBudgetRemaining: null,
        backendPendingTransferCost: 0,
        backendTransfersUsed: null,
      });
    },
    setTransferRows: rows => {
      set({
        transferRows: rows,
      });
    },
    addTransferOutRow: (player, explicitPlayerType) => {
      set(state => {
        const hasUnconfirmedRow = state.transferRows.some(row => {
          return String(row.outPlayer.id) === String(player.id) && !row.confirmed;
        });
        const unconfirmedTransferCount = state.transferRows.filter(row => !row.confirmed).length;
        if (hasUnconfirmedRow || unconfirmedTransferCount >= MAX_TRANSFERS_PER_CONFIRMATION) {
          return {};
        }

        const playerType: TeamTransferPlayerType =
          explicitPlayerType ??
          (String(state.captainPlayerId ?? '') === String(player.id)
            ? 'captain'
            : String(state.viceCaptainPlayerId ?? '') === String(player.id)
              ? 'viceCaptain'
              : 'player');

        return {
          transferRows: [
            ...state.transferRows,
            { outPlayer: player, confirmed: false, playerType },
          ],
        };
      });
    },
    setTransferInForOutPlayer: (outPlayerId, inPlayer) => {
      set(state => {
        let didUpdate = false;
        const transferRows = state.transferRows.map(row => {
          if (String(row.outPlayer.id) !== String(outPlayerId) || row.confirmed) {
            return row;
          }

          didUpdate = true;
          return {
            ...row,
            inPlayer,
            confirmed: false,
          };
        });

        if (!didUpdate) {
          return {};
        }

        return {
          transferRows,
        };
      });
    },
    markPendingTransfersConfirmed: () => {
      set(state => {
        const transferRows = state.transferRows.map(row => {
          if (row.confirmed || row.inPlayer === undefined) {
            return row;
          }

          return {
            ...row,
            confirmed: true,
          };
        });

        return {
          transferRows,
        };
      });
    },
    applyConfirmedTransfers: transfers => {
      if (!transfers.length) {
        return;
      }

      set(state => {
        const preview = previewConfirmedTransfers({
          formation: state.formation,
          slotPositionByIndex: state.slotPositionByIndex,
          playersBySlot: state.playersBySlot,
          transfers,
        });

        const selectedPlayerIds = new Set(
          [...preview.playersBySlot.values()]
            .map(player => player?.id)
            .filter((playerId): playerId is Player['id'] => playerId !== undefined)
        );

        const transferInByTransferOutId = new Map(
          transfers.map(transfer => [String(transfer.outPlayerId), transfer.inPlayer.id])
        );
        const getTransferredCaptaincyPlayerId = (playerId: Player['id'] | undefined) => {
          if (playerId === undefined) {
            return undefined;
          }

          return selectedPlayerIds.has(playerId)
            ? playerId
            : transferInByTransferOutId.get(String(playerId));
        };

        const captainPlayerId = getTransferredCaptaincyPlayerId(state.captainPlayerId);
        const viceCaptainPlayerId = getTransferredCaptaincyPlayerId(state.viceCaptainPlayerId);

        return {
          formation: preview.formation,
          slotPositionByIndex: preview.slotPositionByIndex,
          playersBySlot: preview.playersBySlot,
          captainPlayerId,
          viceCaptainPlayerId,
          isTeamSavedToDatabase: true,
        };
      });
    },
    hydrateFromBackendTeam: payload => {
      const currentBackendTeamId = get().backendTeamId;
      const slotMaps = buildSlotMaps(payload.formation);
      const nextPlayersBySlot = new Map<number, Player | undefined>();

      slotMaps.slotPositionByIndex.forEach((_, slotIndex) => {
        nextPlayersBySlot.set(slotIndex, payload.playersBySlot.get(slotIndex));
      });

      set({
        backendTeamId: payload.teamId ?? currentBackendTeamId,
        formation: payload.formation,
        teamName: payload.teamName.trim() || DEFAULT_TEAM_NAME,
        slotPositionByIndex: slotMaps.slotPositionByIndex,
        playersBySlot: nextPlayersBySlot,
        activeSlotIndex: undefined,
        isTeamSavedToDatabase: true,
        teamCreateStep: 'build',
        captainPlayerId: payload.captainPlayerId,
        viceCaptainPlayerId: payload.viceCaptainPlayerId,
        transferRows: [],
        backendBudgetRemaining: payload.budgetRemaining,
        backendPendingTransferCost: payload.pendingTransferCost,
        backendTransfersUsed: payload.transfersUsed,
      });
    },
  };
});
